import type { Node as ProseMirrorNode } from 'prosemirror-model'
import { Plugin, PluginKey } from 'prosemirror-state'
import { Decoration, DecorationSet } from 'prosemirror-view'

export const kMatterArtifactSelectionHighlightClass = 'matter-artifact-selection-highlight'

export type MatterArtifactSelectionHighlightRange = { from: number; to: number; head?: number }

type MatterArtifactSelectionHighlightState = {
  range: { from: number; to: number; head: number } | null
  decorations: DecorationSet
}

export const matterArtifactSelectionHighlightKey = new PluginKey<MatterArtifactSelectionHighlightState>('matterArtifactSelectionHighlight')

const kEmptyHighlightState: MatterArtifactSelectionHighlightState = { range: null, decorations: DecorationSet.empty }

function buildHighlightState(doc: ProseMirrorNode, range: { from: number; to: number; head: number }): MatterArtifactSelectionHighlightState {
  const decorations: Decoration[] = [
    Decoration.inline(range.from, range.to, {
      class: kMatterArtifactSelectionHighlightClass,
    }),
  ]
  doc.nodesBetween(range.from, range.to, (node, position) => {
    if (node.type.name === 'referenceMarker') {
      decorations.push(Decoration.node(position, position + node.nodeSize, {}, { class: kMatterArtifactSelectionHighlightClass }))
    }
  })
  return { range, decorations: DecorationSet.create(doc, decorations) }
}

/**
 * Owns the highlighted range. Senders dispatch a range once (or null to clear); afterwards the
 * plugin rides every document change through the transaction mapping, so the highlight stays
 * glued to the same text through edits and clears itself the moment that text is replaced. No
 * caller ever re-sends positions, which is what previously let stale cached coordinates reach a
 * changed document (PAXTON-APP-9B6).
 */
export const matterArtifactSelectionHighlightPlugin = new Plugin<MatterArtifactSelectionHighlightState>({
  key: matterArtifactSelectionHighlightKey,
  state: {
    init() {
      return kEmptyHighlightState
    },
    apply(transaction, highlightState) {
      const command = transaction.getMeta(matterArtifactSelectionHighlightKey) as MatterArtifactSelectionHighlightRange | null | undefined
      if (command === null) return kEmptyHighlightState
      if (command !== undefined) {
        // Backstop for any sender whose captured positions are already stale for this document:
        // rebuilding from an out-of-bounds range walks past the document and throws, which
        // unmounts the route. Drop the highlight instead.
        if (command.from < 0 || command.to <= command.from || command.to > transaction.doc.content.size) return kEmptyHighlightState
        return buildHighlightState(transaction.doc, { from: command.from, to: command.to, head: command.head ?? command.to })
      }

      if (!highlightState.range || !transaction.docChanged) return highlightState

      const from = transaction.mapping.mapResult(highlightState.range.from, 1)
      const to = transaction.mapping.mapResult(highlightState.range.to, -1)
      if ((from.deleted && to.deleted) || to.pos <= from.pos) return kEmptyHighlightState
      return buildHighlightState(transaction.doc, { from: from.pos, to: to.pos, head: transaction.mapping.map(highlightState.range.head) })
    },
  },
  props: {
    decorations(state) {
      return matterArtifactSelectionHighlightKey.getState(state)?.decorations
    },
  },
})
